import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {selectUserPage} from '../../store/user/selectors/user.selectors';
import {Store} from '@ngrx/store';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {ReplaySubject, Subject, take, takeUntil} from 'rxjs';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {CustomValidators} from '../../validator/custom-validator';
import {QuestionOptions} from '../../constants/question-options';
import {UserService} from '../../services/user/user.service';
import {UserPage} from '../../model/user-page';
import {LineOfBusiness} from '../../model/proposal/line-of-business';
import {Roles} from '../../enum/roles';
import {TranslateService} from '@ngx-translate/core';
import {ExpertService} from '../../model/expert-service';

@Component({
    selector: 'app-profile-user-edit',
    templateUrl: './profile-user-edit.component.html',
    styleUrls: ['../../../styles/form-input.scss', '../../../styles/edit-style.scss'],
    standalone: false
})
export class ProfileUserEditComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  QuestionOptions = QuestionOptions;
  userPage$ = this.userStore.select(selectUserPage);
  userId: string;
  Roles = Roles;
  formGroup = this.fb.group({
    firstName: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    lastName: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    email: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    mobileNumber: '',
    telephoneNumber: '',
    street: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    houseNumber: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    postalCode: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    location: ['', [Validators.required, CustomValidators.notOnlyWhitespace]],
    introductionText: '',
    lookingForLOBs: [[] as string[]],
    offeringLOBs: [[] as string[]],
    expertServices: [[] as string[]],

  });

  protected destroy$ = new Subject<void>();

  public filterCtrlLineOfBusinessOffering: FormControl<string> = new FormControl<string>('');
  public filterCtrlLineOfBusinessLooking: FormControl<string> = new FormControl<string>('');
  public filterCtrlExpertServices: FormControl<string> = new FormControl<string>('');
  public filteredOptionsLineOfBusinessOffering: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  public filteredOptionsLineOfBusinessLooking: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  public filteredOptionsExpertServices: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  public allLOBsSelectedLooking = false;
  public allLOBsSelectedOffering = false;
  public allExpertServicesSelected = false;

  @Output() updated = new EventEmitter<void>();

  constructor(public userStore: Store<UserAppState>, private readonly fb: FormBuilder, private readonly userService: UserService,  private readonly translate: TranslateService) {
  }

  ngOnInit(): void {
    this.userPage$.pipe(take(1)).subscribe(
      userPage => {
        this.formGroup.controls.firstName.setValue(userPage.firstName);
        this.formGroup.controls.lastName.setValue(userPage.lastName);
        this.formGroup.controls.email.setValue(userPage.email);
        this.formGroup.controls.mobileNumber.setValue(userPage.mobileNumber);
        this.formGroup.controls.telephoneNumber.setValue(userPage.telephoneNumber);
        this.formGroup.controls.street.setValue(userPage.street);
        this.formGroup.controls.houseNumber.setValue(userPage.houseNumber);
        this.formGroup.controls.postalCode.setValue(userPage.postalCode);
        this.formGroup.controls.location.setValue(userPage.location);
        this.formGroup.controls.introductionText.setValue(userPage.introductionText);
        this.formGroup.controls.offeringLOBs.setValue(userPage.offeringLOBs.map((lob) => lob.number));
        this.formGroup.controls.lookingForLOBs.setValue(userPage.lookingForLOBs.map((lob) => lob.number));
        this.formGroup.controls.expertServices.setValue(userPage.offeringExpertServices.map((serv) => serv.number));
        this.userId = userPage.id;
      }
    );

    this.filteredOptionsLineOfBusinessOffering.next(this.QuestionOptions.lineOfBusiness.slice());
    this.filteredOptionsLineOfBusinessLooking.next(this.QuestionOptions.lineOfBusiness.slice());
    this.filteredOptionsExpertServices.next(this.QuestionOptions.expertServices.slice());

    this.filterCtrlLineOfBusinessOffering.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterLineOfBusiness(this.filterCtrlLineOfBusinessOffering.value, this.QuestionOptions.lineOfBusiness, this.filteredOptionsLineOfBusinessOffering);
      });

    this.filterCtrlLineOfBusinessLooking.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterLineOfBusiness(this.filterCtrlLineOfBusinessLooking.value, this.QuestionOptions.lineOfBusiness, this.filteredOptionsLineOfBusinessLooking);
      });

    this.filterCtrlExpertServices.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterExpertService(this.filterCtrlExpertServices.value, this.QuestionOptions.expertServices, this.filteredOptionsExpertServices);
      });
  }

  submit() {
    const userPage = new UserPage(this.formGroup.getRawValue());
    userPage.lookingForLOBs = [];

    this.formGroup.getRawValue().lookingForLOBs.forEach((number) => {
      let lob = new LineOfBusiness();
      lob.number = number;
      userPage.lookingForLOBs.push(lob);
    });
    userPage.offeringLOBs = [];
    this.formGroup.getRawValue().offeringLOBs.forEach((number) => {
      let lob = new LineOfBusiness();
      lob.number = number;
      userPage.offeringLOBs.push(lob);
    });
    userPage.offeringExpertServices = [];
    this.formGroup.getRawValue().expertServices.forEach((number) => {
      let serv = new ExpertService();
      serv.number = number;
      userPage.offeringExpertServices.push(serv);
    });

    this.userService.updateUserProfile(this.userId, userPage).subscribe(
      _ => this.updated.emit()
    );
  }

  emitClose() {
    this.close.emit();
  }

  filterLineOfBusiness(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(lineOfBusiness => this.translateLineOfBusiness(lineOfBusiness).toLowerCase().indexOf(search) > -1)
    );
  }

  translateLineOfBusiness(lineOfBusiness): string {
    return lineOfBusiness + '-' + this.translate.instant('lineOfBusiness.clauses.' + lineOfBusiness + '.name');
  }

  filterExpertService(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(expertService => this.translateExpertService(expertService).toLowerCase().indexOf(search) > -1)
    );
  }

  translateExpertService(expertService): string {
    return expertService + '-' + this.translate.instant('expertServices.clauses.' + expertService + '.name');
  }

  toggleAllLooking(): void {
    if (this.allLOBsSelectedLooking) {
      this.formGroup.controls.lookingForLOBs.setValue([]);
    } else {
      this.formGroup.controls.lookingForLOBs.setValue([...this.QuestionOptions.lineOfBusiness]);
    }
    this.allLOBsSelectedLooking = !this.allLOBsSelectedLooking;
  }

  toggleAllOffering(): void {
    if (this.allLOBsSelectedOffering) {
      this.formGroup.controls.offeringLOBs.setValue([]);
    } else {
      this.formGroup.controls.offeringLOBs.setValue([...this.QuestionOptions.lineOfBusiness]);
    }
    this.allLOBsSelectedOffering = !this.allLOBsSelectedOffering;
  }

  toggleAllExpertServices(): void {
    if (this.allExpertServicesSelected) {
      this.formGroup.controls.expertServices.setValue([]);
    } else {
      this.formGroup.controls.expertServices.setValue([...this.QuestionOptions.expertServices]);
    }
    this.allExpertServicesSelected = !this.allExpertServicesSelected;
  }
}
